import type { Permission } from '../../api/types/permissions';

/** Item de un menú agrupado. `To` se especializa al subconjunto de rutas de cada sección. */
export interface NavItemDef<To extends string = string> {
    to: To;
    /** Permiso que ya gateaba el item (el scope lo fija el render por sección). */
    permission: Permission;
    /** Clave i18n del label (namespace common). */
    labelKey: string;
}

/** Grupo (categoría colapsable) de un menú. `Id` = ids estables de las categorías. */
export interface NavGroupDef<Id extends string = string, To extends string = string> {
    id: Id;
    /** Clave i18n del título del grupo (namespace common). */
    titleKey: string;
    items: NavItemDef<To>[];
}

/**
 * Id del grupo cuyo item matchea el pathname (para auto-abrir el grupo activo). Match por prefijo:
 * cubre el item EXACTO y sus descendientes (`/x/123`). Devuelve null si la ruta no cae en ningún grupo.
 */
export function activeGroupId<Id extends string, To extends string>(
    groups: readonly NavGroupDef<Id, To>[],
    pathname: string,
): Id | null {
    for (const group of groups) {
        for (const item of group.items) {
            if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
                return group.id;
            }
        }
    }
    return null;
}

/**
 * Grupos visibles con sus items filtrados por permiso. Un grupo cuyos items quedan TODOS gateados-out
 * NO aparece (evita el header huérfano). `canSee` replica el chequeo del <Gate> de la sección.
 */
export function visibleNavGroups<Id extends string, To extends string>(
    groups: readonly NavGroupDef<Id, To>[],
    canSee: (permission: Permission) => boolean,
): NavGroupDef<Id, To>[] {
    return groups
        .map((group) => ({ ...group, items: group.items.filter((i) => canSee(i.permission)) }))
        .filter((group) => group.items.length > 0);
}
